// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import {
  convertPressure,
  convertTemperature,
  formatNumber,
  formatValueWithUnit,
} from '../../../../../../plugins/general';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Types
import {
  MeasurementType,
  PressureType,
  TemperatureType,
} from '../../../../../../types/global';

// Components
import { CardSubItem } from '../../../../../../layout/elements/CardList/CardList.types';
import { EquipmentSensorListItem } from '../../../../../../components/EquipmentSensorList';
import {
  MaintenanceCardList,
  MaintenanceCardItem,
} from '../../../../../../components/MaintenanceCardList';
// Modules
import { ListModule } from '../ListModule';

// Component
import { SensorScalingProps } from './SensorScaling.types';
import {
  Container,
  ListCol,
  ListContainer,
  Separator,
} from './SensorScaling.style';

const mapSensorScalingToSubItems = (
  items: EquipmentSensorListItem[],
): CardSubItem[] => items.map((item: EquipmentSensorListItem) => ({
  label: item.sensor as string,
  secondaryValue: item.min as string,
  value: item.max as string,
}));

function SensorScaling({
  className,
  unitData,
  testId,
}: SensorScalingProps): JSX.Element | null {
  // Dependencies
  const { t } = useTranslation();
  const { isXs, isSm, isMd } = useBreakpoint();
  const isMobile: boolean = isXs || isSm || isMd;

  const pressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);
  const temperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);

  const lubItems: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.sensorScaling.tankTempDescription'),
      min: formatNumber(convertTemperature(
        unitData?.spTankTempScaleMin,
        temperatureUnit,
        0,
      ).value),
      max: convertTemperature(
        unitData?.spTankTempScaleMax,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.sensorScaling.returnTempDescription'),
      min: formatNumber(convertTemperature(
        unitData?.spReturnTempScaleMin,
        temperatureUnit,
        0,
      ).value),
      max: convertTemperature(
        unitData?.spReturnTempScaleMax,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.sensorScaling.supplyPressureDescription'),
      min: formatNumber(convertPressure(
        unitData?.spSupplyPressureScaleMin,
        pressureUnit,
        0,
      ).value),
      max: convertPressure(
        unitData?.spSupplyPressureScaleMax,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    ...(unitData?.eccentricBushingTempEnable ? [{
      sensor: t('view.crusher.maintenance.sensorScaling.eccentricBushingTempDescription'),
      min: formatNumber(convertTemperature(
        unitData?.spEccentricBushingScaleMin,
        temperatureUnit,
        0,
      ).value),
      max: convertTemperature(
        unitData?.spEccentricBushingScaleMax,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    }] : []),
    ...(unitData?.counterShaftBushingTempEnable ? [{
      sensor: t('view.crusher.maintenance.sensorScaling.countershaftBushingTempDescription'),
      min: formatNumber(convertTemperature(
        unitData?.spCounterShaftBushingScaleMin,
        temperatureUnit,
        0,
      ).value),
      max: convertTemperature(
        unitData?.spCounterShaftBushingScaleMax,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    }] : []),
    ...(unitData?.lubeSupplyTempEnable ? [{
      sensor: t('view.crusher.maintenance.sensorScaling.supplyTempDescription'),
      min: formatNumber(convertTemperature(
        unitData?.spSupplyTempScaleMin,
        temperatureUnit,
        0,
      ).value),
      max: convertTemperature(
        unitData?.spSupplyTempScaleMax,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    }] : []),
  ];

  const crusherItems: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.sensorScaling.motorCurrentDescription'),
      min: formatValueWithUnit(unitData?.spCrusherMotorAmpsScaleMin, ''),
      max: formatValueWithUnit(unitData?.spCrusherMotorAmpsScaleMax, 'A'),
    },
    {
      sensor: t('view.crusher.maintenance.sensorScaling.motorRpmDescription'),
      min: formatValueWithUnit(unitData?.spCrusherMotorRpmScaleMin, ''),
      max: formatValueWithUnit(unitData?.spCrusherMotorRpmScaleMax, 'RPM'),
    },
    {
      sensor: t('view.crusher.maintenance.sensorScaling.vibrationDescription'),
      min: formatValueWithUnit(unitData?.spVibScaleMin, ''),
      max: formatValueWithUnit(unitData?.spVibScaleMax, '%'),
    },
  ];

  const hydraulicItems: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.sensorScaling.systemPressureDescription'),
      min: formatNumber(convertPressure(
        unitData?.spSystemPressureScaleMin,
        pressureUnit,
        0,
      ).value),
      max: convertPressure(
        unitData?.spSystemPressureScaleMax,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.sensorScaling.clampPressureDescription'),
      min: formatNumber(convertPressure(
        unitData?.spClampPressureScaleMin,
        pressureUnit,
        0,
      ).value),
      max: convertPressure(
        unitData?.spClampPressureScaleMax,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.sensorScaling.trampPressureDescription'),
      min: formatNumber(convertPressure(
        unitData?.spTrampPressureScaleMin,
        pressureUnit,
        0,
      ).value),
      max: convertPressure(
        unitData?.spTrampPressureScaleMax,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  if (isMobile) {
    const cardItems: MaintenanceCardItem[] = [
      {
        key: 'lubrication',
        title: t('view.crusher.maintenance.sensorScaling.lubricationListTitle'),
        subItems: mapSensorScalingToSubItems(lubItems),
      },
      {
        key: 'crusher',
        title: t('view.crusher.maintenance.sensorScaling.crusherListTitle'),
        subItems: mapSensorScalingToSubItems(crusherItems),
      },
      {
        key: 'hydraulic',
        title: t('view.crusher.maintenance.sensorScaling.hydraulicListTitle'),
        subItems: mapSensorScalingToSubItems(hydraulicItems),
      },
    ];

    return (
      <Container className={className} data-testid={testId}>
        <Separator />
        <MaintenanceCardList
          columnLabels={{
            left: t('common.sensor'),
            center: `${t('common.min')}`,
            right: t('common.max'),
          }}
          items={cardItems}
          testId={`${testId}-card-list`}
        />
      </Container>
    );
  }

  return (
    <Container className={className} data-testid={testId}>
      <Separator />
      <ListContainer>
        <ListCol xs={24} lg={12}>
          <ListModule
            title={t('view.crusher.maintenance.sensorScaling.lubricationListTitle')}
            items={lubItems}
          />
        </ListCol>
        <ListCol xs={24} lg={12}>
          <ListModule
            title={t('view.crusher.maintenance.sensorScaling.crusherListTitle')}
            items={crusherItems}
          />
        </ListCol>
        <ListCol xs={24} lg={12}>
          <ListModule
            title={t('view.crusher.maintenance.sensorScaling.hydraulicListTitle')}
            items={hydraulicItems}
          />
        </ListCol>
      </ListContainer>
    </Container>
  );
}

export { SensorScaling };
